/**
 * ChatWorkspace – unified chat/task workspace.
 *
 * Layout (3 columns, mimicking ChatGPT × Manus):
 *
 *   ┌──────────────┬─────────────────────────────┬──────────────────┐
 *   │ ChatHistory  │  Message stream              │ TaskDetailRail   │
 *   │ Panel        │  (Bubble.List equivalent)    │  (steps/files/   │
 *   │ (260 px)     │                              │   approvals)     │
 *   │              │  ─────────────────────────── │  (320 px)        │
 *   │              │  Sender (textarea + actions) │                  │
 *   └──────────────┴─────────────────────────────┴──────────────────┘
 *
 * Key behaviours:
 *  - Session IDs are persisted in URL hash (#chat/<id>) as well as state.
 *  - When a message escalates into a task the task detail rail slides in and
 *    the session keeps scrolling as the task progresses.
 *  - Assistant messages expose: Copy · Add to KB · Create follow-up · Sources.
 *  - FollowUpCard appears automatically when a task reaches a terminal state.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Badge, Button, Drawer, Input, Modal, Select, Skeleton, Space, Tag, Tooltip, Typography, message,
} from 'antd';
import {
  AudioOutlined, BookOutlined, CheckOutlined, CloseOutlined, CopyOutlined,
  FileOutlined, FolderOpenOutlined,
  LinkOutlined, LoadingOutlined, PaperClipOutlined, PlayCircleOutlined,
  PlusOutlined, RightOutlined, SendOutlined, StopOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { GoogleUser } from '../App';
import type { ChatMessage } from '../api/client';
import * as client from '../api/client';
import {
  sendChatMessage,
  launchTaskFromChat,
  readSessionFromHash,
  writeSessionToHash,
} from '../api/taskApiService';
import type { ChatSessionSummary } from './ChatHistoryPanel';
import DictationOverlay from './DictationOverlay';
import KbAddDrawer from './KbAddDrawer';
import TaskDetailRail from './TaskDetailRail';

const { Text } = Typography;

// Pill button style shared across the app (matches FinalResultPanel actions).
const PILL_BORDER_COLOR = '#3f3f3f';
const PILL_BACKGROUND = '#2a2a2a';
const getPillButtonStyle = (color: string): React.CSSProperties => ({
  background: PILL_BACKGROUND,
  borderColor: PILL_BORDER_COLOR,
  borderRadius: 20,
  color,
  fontSize: 11,
});

// ── Storage keys (same format as AgentDashboard so sessions are shared) ───────

const CHAT_MESSAGES_KEY = 'praxys_chat_messages_v2';
const CHAT_SESSIONS_KEY = 'praxys_chat_sessions_v1';
const CHAT_ACTIVE_SESSION_KEY = 'praxys_chat_active_session_v1';
const CHAT_KB_SEARCH_LIMIT = 6;

// ── Types ─────────────────────────────────────────────────────────────────────

interface KbOption {
  id: string;
  name: string;
  description?: string;
  document_count?: number;
  chunk_count?: number;
}

interface ActiveTask {
  taskId: string;
  messageIndex: number; // the user message index that launched this task
}

interface KbDraftState {
  open: boolean;
  messageIndex: number;
  content: string;
}

interface SourcesState {
  open: boolean;
  messageIndex: number;
}

/** A file attached during this browser session (chat or task). */
interface SessionFile {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
  addedAt: string;   // ISO timestamp
  contextType: 'chat';
  contextTitle: string; // session title at time of upload
  driveUrl?: string;    // populated after GDrive auto-upload
}

type DictationStatus = 'idle' | 'loading' | 'recording' | 'stopped' | 'error' | 'permission-denied';

// SpeechRecognition browser types (not in TS lib by default)
type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;
interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onstart: (() => void) | null;
  onresult: ((e: any) => void) | null;
  onerror: ((e: any) => void) | null;
  onend: (() => void) | null;
}

interface Props {
  user: GoogleUser | null;
  onTaskLaunched?: (taskId: string) => void;
  /** Called when the user clicks "Open Task" in a task reference card. */
  onOpenTask?: (taskId: string) => void;
  isLightMode?: boolean;
  availableKbs?: KbOption[];
  /** Session ID to activate when changed from the parent (sidebar click). */
  externalSessionId?: string;
  externalSessions?: ChatSessionSummary[];
  onSessionChange?: (sessionId: string) => void;
  onSessionsChange?: (sessions: ChatSessionSummary[]) => void;
  /** Project context used when creating tasks from messages. */
  projectId?: string;
}

// ── LocalStorage helpers ──────────────────────────────────────────────────────

const _key = (base: string, userId: string) => `${base}:${userId.trim().toLowerCase()}`;
const _msgKey = (userId: string, sid: string) => `${_key(CHAT_MESSAGES_KEY, userId)}:${sid}`;

const loadSessions = (userId: string): ChatSessionSummary[] => {
  try {
    const raw = localStorage.getItem(_key(CHAT_SESSIONS_KEY, userId));
    const parsed = JSON.parse(raw ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

const saveSessions = (userId: string, sessions: ChatSessionSummary[]) => {
  localStorage.setItem(_key(CHAT_SESSIONS_KEY, userId), JSON.stringify(sessions));
};

const loadMessages = (userId: string, sessionId: string): ChatMessage[] => {
  try {
    const raw = localStorage.getItem(_msgKey(userId, sessionId));
    const parsed = JSON.parse(raw ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

const saveMessages = (userId: string, sessionId: string, msgs: ChatMessage[]) => {
  localStorage.setItem(_msgKey(userId, sessionId), JSON.stringify(msgs));
};

const loadActiveSessionId = (userId: string): string => {
  try { return localStorage.getItem(_key(CHAT_ACTIVE_SESSION_KEY, userId)) ?? ''; } catch { return ''; }
};
const saveActiveSessionId = (userId: string, id: string) => {
  localStorage.setItem(_key(CHAT_ACTIVE_SESSION_KEY, userId), id);
};

const loadArchivedIds = (_userId: string): Set<string> => new Set();const generateId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const makeSession = (title = 'New Chat'): ChatSessionSummary => ({
  id: generateId(),
  title,
  updatedAt: new Date().toISOString(),
});

// ── ChatWorkspace ─────────────────────────────────────────────────────────────

const ChatWorkspace: React.FC<Props> = ({
  user,
  onTaskLaunched,
  onOpenTask,
  isLightMode = true,
  availableKbs = [],
  externalSessionId,
  externalSessions = [],
  onSessionChange,
  onSessionsChange,
  projectId,
}) => {
  const userId = user?.email ?? '';

  // ── Sessions ──────────────────────────────────────────────────────────────
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [activeSessionId, setActiveSessionId] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // ── Sending state ─────────────────────────────────────────────────────────
  const [input, setInput] = useState('');
  const [chatSubmitting, setChatSubmitting] = useState(false);
  const [taskSubmitting, setTaskSubmitting] = useState(false);

  // ── KB selection ──────────────────────────────────────────────────────────
  const [chatKbIds, setChatKbIds] = useState<string[]>([]);

  // ── Active task ───────────────────────────────────────────────────────────
  const [activeTask, setActiveTask] = useState<ActiveTask | null>(null);
  const [taskRailVisible, setTaskRailVisible] = useState(false);
  const [followUpSubmitting, setFollowUpSubmitting] = useState(false);

  // ── Per-message UI state ──────────────────────────────────────────────────
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [kbDraft, setKbDraft] = useState<KbDraftState>({ open: false, messageIndex: -1, content: '' });
  const [sources, setSources] = useState<SourcesState>({ open: false, messageIndex: -1 });

  // ── Create Task from Message modal ────────────────────────────────────────
  const [createTaskModal, setCreateTaskModal] = useState<{
    open: boolean;
    title: string;
    instructions: string;
    submitting: boolean;
  }>({ open: false, title: '', instructions: '', submitting: false });

  const handleCreateTaskFromMessage = useCallback((content: string) => {
    const title = content.slice(0, 100).replace(/\n/g, ' ').trim();
    setCreateTaskModal({ open: true, title, instructions: content, submitting: false });
  }, []);

  const handleSubmitTaskFromMessage = useCallback(async () => {
    const { title, instructions } = createTaskModal;
    if (!title.trim()) { message.warning('Please provide a task title'); return; }
    setCreateTaskModal(prev => ({ ...prev, submitting: true }));
    try {
      const resp = await client.executeTask(
        instructions || title,
        'autonomous',
        '',
        userId,
        '',
        { source_chat_id: activeSessionId || '' },
        chatKbIds,
        projectId || '',
      );
      const taskId = resp.data?.task?.id || resp.data?.id || '';
      const taskMsgIdx = messages.length;
      const taskRef: ChatMessage = {
        role: 'assistant',
        content: `📋 **Task created** — ID: \`${taskId}\`\n\n**Title:** ${title.slice(0, 80)}`,
      };
      const updated = [...messages, taskRef];
      setMessages(updated);
      if (userId) saveMessages(userId, activeSessionId, updated);
      setCreateTaskModal({ open: false, title: '', instructions: '', submitting: false });
      setActiveTask({ taskId, messageIndex: taskMsgIdx });
      setTaskRailVisible(true);
      message.success('Task created and executing');
      onTaskLaunched?.(taskId);
    } catch (e: any) {
      message.error('Failed to create task: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
      setCreateTaskModal(prev => ({ ...prev, submitting: false }));
    }
  }, [createTaskModal, userId, activeSessionId, projectId, chatKbIds, messages, onTaskLaunched]);

  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const hydratedRef = useRef(false);

  // ── Chat attachment ───────────────────────────────────────────────────────
  const [attachedFile, setAttachedFile] = useState<File | null>(null);

  // ── Session file library ──────────────────────────────────────────────────
  const [sessionFiles, setSessionFiles] = useState<SessionFile[]>([]);
  const [libraryDrawerOpen, setLibraryDrawerOpen] = useState(false);

  // ── Dictation (local SpeechRecognition) ───────────────────────────────────
  const [dictationStatus, setDictationStatus] = useState<DictationStatus>('idle');
  const [dictationTranscript, setDictationTranscript] = useState('');
  const [dictationError, setDictationError] = useState('');
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const dictationTranscriptRef = useRef('');
  const dictationCommitRef = useRef(false);
  const dictationErrorRef = useRef('');

  const handleDictationToggle = useCallback(() => {
    const SR: SpeechRecognitionConstructor | undefined =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      message.error('Speech recognition is not supported in this browser.');
      return;
    }

    if (dictationStatus === 'recording') {
      dictationCommitRef.current = true;
      recognitionRef.current?.stop();
      return;
    }

    setDictationError('');
    dictationErrorRef.current = '';
    setDictationTranscript('');
    dictationTranscriptRef.current = '';
    dictationCommitRef.current = false;

    setDictationStatus('loading');
    const rec = new SR();
    recognitionRef.current = rec;
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    rec.onstart = () => setDictationStatus('recording');
    rec.onresult = (e: any) => {
      let full = '';
      for (let i = 0; i < e.results.length; i++) {
        full += e.results[i][0].transcript;
      }
      dictationTranscriptRef.current = full;
      setDictationTranscript(full);
    };
    rec.onerror = (e: any) => {
      const msg = e.error === 'not-allowed' ? 'Microphone permission denied.' : `Recognition error: ${e.error}`;
      dictationErrorRef.current = msg;
      setDictationError(msg);
      setDictationStatus(e.error === 'not-allowed' ? 'permission-denied' : 'error');
    };
    rec.onend = () => {
      const committed = dictationTranscriptRef.current.trim();
      if (committed && !dictationErrorRef.current) {
        setInput(prev => prev.trim() ? prev.trim() + ' ' + committed : committed);
      }
      setDictationStatus('idle');
      setDictationTranscript('');
      dictationTranscriptRef.current = '';
      recognitionRef.current = null;
    };

    try {
      rec.start();
    } catch {
      setDictationStatus('error');
      setDictationError('Could not start microphone.');
    }
  }, [dictationStatus]);

  // ── Google Drive integration ───────────────────────────────────────────────
  const [gdriveInstanceId, setGdriveInstanceId] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return undefined;
    const refreshGdriveStatus = () => {
      const req = client.getGDriveStatus(userId);
      if (req && typeof req.then === 'function') {
        req.then((res: any) => {
          const inst = res?.data?.instance;
          if (inst?.id && res?.data?.connected) setGdriveInstanceId(inst.id);
          else setGdriveInstanceId(null);
        }).catch(() => {/* no GDrive connected */});
      }
    };
    refreshGdriveStatus();
    window.addEventListener('praxys:connector-oauth-complete', refreshGdriveStatus);
    return () => window.removeEventListener('praxys:connector-oauth-complete', refreshGdriveStatus);
  }, [userId]);

  useEffect(() => {
    if (externalSessions.length === 0) return;
    const next = [...externalSessions].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
    setSessions(next);
  }, [externalSessions]);

  // ── Hydrate on mount ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId || hydratedRef.current) return;
    hydratedRef.current = true;

    let allSessions = loadSessions(userId).sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
    if (allSessions.length === 0) {
      const first = makeSession();
      allSessions = [first];
      saveSessions(userId, allSessions);
    }
    setSessions(allSessions);

    // Priority: URL hash → externalSessionId → localStorage → first session
    const hashId = readSessionFromHash();
    const startId = hashId || externalSessionId || loadActiveSessionId(userId) || allSessions[0].id;
    activateSession(startId, allSessions);
  }, [userId]); // intentionally omits activateSession to run only on mount

  const activateSession = useCallback(
    (id: string, allSessions: ChatSessionSummary[]) => {
      const exists = allSessions.some(s => s.id === id);
      let nextSessions = allSessions;
      if (!exists && id) {
        const placeholder: ChatSessionSummary = { id, title: 'New Chat', updatedAt: new Date().toISOString() };
        nextSessions = [placeholder, ...allSessions].sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );
        setSessions(nextSessions);
        if (userId) saveSessions(userId, nextSessions);
        onSessionsChange?.(nextSessions);
      }
      const resolvedId = id || (nextSessions[0]?.id ?? '');
      if (!resolvedId) return;
      setActiveSessionId(resolvedId);
      setMessages(loadMessages(userId, resolvedId));
      setInput('');
      setActiveTask(null);
      setTaskRailVisible(false);
      writeSessionToHash(resolvedId);
      if (userId) saveActiveSessionId(userId, resolvedId);
      onSessionChange?.(resolvedId);
    },
    [onSessionChange, onSessionsChange, userId],
  );

  // ── Sync external session ID from parent (sidebar click) ──────────────────
  useEffect(() => {
    if (!externalSessionId || externalSessionId === activeSessionId) return;
    activateSession(externalSessionId, sessions);
  }, [externalSessionId]); // intentionally omits activateSession and sessions

  const upsertSession = useCallback(
    (id: string, title: string) => {
      const updated: ChatSessionSummary = {
        id,
        title: title.slice(0, 80) || 'New Chat',
        updatedAt: new Date().toISOString(),
      };
      const others = sessions.filter(s => s.id !== id);
      const next = [updated, ...others].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      setSessions(next);
      if (userId) saveSessions(userId, next);
      onSessionsChange?.(next);
    },
    [sessions, onSessionsChange, userId],
  );

  // ── New session (used externally via sidebar) ────────────────────────────
  // Kept reference-only — session creation lives in the right-side nav now.

  // ── Scroll to bottom ──────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatSubmitting]);

  // ── Latest user prompt (for task launch) ──────────────────────────────────
  const latestPrompt = useMemo(
    () =>
      [...messages]
        .reverse()
        .find(m => m.role === 'user')?.content.trim() ?? '',
    [messages],
  );

  const questionMarkers = useMemo(
    () => messages
      .map((msg, index) => ({ msg, index }))
      .filter(({ msg }) => msg.role === 'user'),
    [messages],
  );

  const scrollToQuestion = useCallback((index: number) => {
    messageRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // ── Send chat message ─────────────────────────────────────────────────────
  const TEXTUAL_FILE_EXTENSIONS = new Set([
    'txt', 'md', 'markdown', 'csv', 'tsv', 'json', 'jsonl', 'log',
    'yaml', 'yml', 'xml', 'html', 'htm', 'js', 'ts', 'tsx', 'jsx',
    'py', 'css', 'scss', 'sql', 'sh', 'ini', 'toml', 'rtf',
  ]);
  const IMAGE_FILE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tif', 'tiff']);

  const isTextualFile = (file: File): boolean => {
    if (file.type && (file.type.startsWith('text/') || file.type === 'application/json')) return true;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return TEXTUAL_FILE_EXTENSIONS.has(ext);
  };

  const isImageFile = (file: File): boolean => {
    if (file.type && file.type.startsWith('image/')) return true;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return IMAGE_FILE_EXTENSIONS.has(ext);
  };

  const readFileAsText = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });

  const buildPromptWithAttachment = async (text: string, file: File | null): Promise<string> => {
    if (!file) return text;
    const MAX_CHARS = 24000; // keep prompt within LLM context
    if (isTextualFile(file)) {
      try {
        const raw = await readFileAsText(file);
        const truncated = raw.length > MAX_CHARS ? `${raw.slice(0, MAX_CHARS)}\n…[truncated]` : raw;
        return `${text}\n\n[Attached file: ${file.name}]\n\`\`\`\n${truncated}\n\`\`\``;
      } catch {
        return `${text}\n\n[Attached file: ${file.name} — could not be read]`;
      }
    }
    try {
      const extractRes = await client.extractTextFromUploadedFile(file, MAX_CHARS);
      const extracted = String(extractRes.data?.text ?? '').trim();
      if (extracted) {
        const label = isImageFile(file) ? 'Attached image analysis' : 'Attached file';
        return `${text}\n\n[${label}: ${file.name}]\n\`\`\`\n${extracted}\n\`\`\``;
      }
    } catch {
      // Fallback to metadata-only prompt when extraction is unavailable.
    }
    return `${text}\n\n[Attached file: ${file.name} (${file.type || 'binary'}, ${Math.round(file.size / 1024)} KB) — binary content not included]`;
  };

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleAttachFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachedFile(file);
      if (!input.trim()) {
        setInput(isImageFile(file) ? `Analyze the attached image ${file.name}` : `Rewrite the attached ${file.name}`);
      }
      // Track in session file library
      const sf: SessionFile = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        file,
        addedAt: new Date().toISOString(),
        contextType: 'chat',
        contextTitle: sessions.find(s => s.id === activeSessionId)?.title ?? 'Chat',
      };
      setSessionFiles(prev => [...prev, sf]);
    }
    e.target.value = '';
  };

  const handleSend = async () => {
    const text = input.trim();
    if ((!text && !attachedFile) || !activeSessionId || chatSubmitting) return;

    const fileToUpload = attachedFile;
    const promptText = await buildPromptWithAttachment(text || `Read and analyze the attached ${attachedFile?.name ?? 'file'}`, attachedFile);
    const displayText = attachedFile
      ? (text ? `${text}\n\n📎 ${attachedFile.name}` : `📎 ${attachedFile.name}`)
      : text;
    const userMsg: ChatMessage = { role: 'user', content: promptText, displayContent: displayText };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setInput('');
    setAttachedFile(null);
    setChatSubmitting(true);
    upsertSession(activeSessionId, (text || attachedFile?.name || '').slice(0, 80));
    if (userId) saveMessages(userId, activeSessionId, newMsgs);

    // Auto-upload to Google Drive in background if connected
    if (fileToUpload && gdriveInstanceId) {
      const sessionTitle = sessions.find(s => s.id === activeSessionId)?.title ?? 'Chat';
      client.uploadFileToConnector(gdriveInstanceId, fileToUpload, 'chat', activeSessionId, sessionTitle)
        .then((res: any) => {
          const url = res?.data?.file?.webViewLink || res?.data?.file?.web_view_link;
          if (url) {
            setSessionFiles(prev => prev.map(f =>
              f.name === fileToUpload.name && !f.driveUrl ? { ...f, driveUrl: url } : f,
            ));
          }
        })
        .catch(() => {/* silent — GDrive upload failure shouldn't block chat */});
    }

    try {
      const reply = await sendChatMessage(newMsgs, {
        sessionId: activeSessionId,
        kbIds: chatKbIds,
        kbSearchLimit: CHAT_KB_SEARCH_LIMIT,
      });
      const withReply: ChatMessage[] = [...newMsgs, { role: 'assistant', content: reply }];
      setMessages(withReply);
      if (userId) saveMessages(userId, activeSessionId, withReply);
      upsertSession(activeSessionId, (text || fileToUpload?.name || '').slice(0, 80));
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const errText = detail
        ? `Sorry, I couldn't respond right now: ${detail}`
        : "Sorry, I couldn't respond right now. Please try again.";
      const withErr: ChatMessage[] = [...newMsgs, { role: 'assistant', content: errText }];
      setMessages(withErr);
      if (userId) saveMessages(userId, activeSessionId, withErr);
    } finally {
      setChatSubmitting(false);
    }
  };

  // ── Launch task from chat ─────────────────────────────────────────────────
  const handleLaunchTask = async () => {
    if (!latestPrompt || taskSubmitting) return;
    setTaskSubmitting(true);
    try {
      const task = await launchTaskFromChat(latestPrompt, {
        userId,
        sessionId: activeSessionId,
        chatHistory: messages,
        kbIds: chatKbIds,
      });
      const taskMsgIdx = messages.length;
      const noticeMsg: ChatMessage = {
        role: 'assistant',
        content: `🚀 Task launched (ID: \`${task.id}\`). Tracking progress in the panel →`,
      };
      const withTask: ChatMessage[] = [...messages, noticeMsg];
      setMessages(withTask);
      if (userId) saveMessages(userId, activeSessionId, withTask);
      upsertSession(activeSessionId, latestPrompt.slice(0, 80));
      setActiveTask({ taskId: task.id, messageIndex: taskMsgIdx });
      setTaskRailVisible(true);
      onTaskLaunched?.(task.id);
      message.success('Task launched');
    } catch (e: any) {
      message.error('Failed to launch task: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
    } finally {
      setTaskSubmitting(false);
    }
  };

  // ── Follow-up from task rail ──────────────────────────────────────────────
  const handleFollowUp = async (text: string) => {
    const userMsg: ChatMessage = { role: 'user', content: text };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setInput('');
    setFollowUpSubmitting(true);
    upsertSession(activeSessionId, text.slice(0, 80));
    if (userId) saveMessages(userId, activeSessionId, newMsgs);

    try {
      // If there's an active task, submit as a task follow-up; otherwise chat
      if (activeTask?.taskId) {
        await client.createTaskFollowUp(activeTask.taskId, text);
        const reply: ChatMessage = {
          role: 'assistant',
          content: `Follow-up submitted to task \`${activeTask.taskId}\`. Check the Follow-ups tab for the response.`,
        };
        const withReply = [...newMsgs, reply];
        setMessages(withReply);
        if (userId) saveMessages(userId, activeSessionId, withReply);
      } else {
        const reply = await sendChatMessage(newMsgs, {
          sessionId: activeSessionId,
          kbIds: chatKbIds,
        });
        const withReply: ChatMessage[] = [...newMsgs, { role: 'assistant', content: reply }];
        setMessages(withReply);
        if (userId) saveMessages(userId, activeSessionId, withReply);
      }
    } catch {
      const withErr: ChatMessage[] = [
        ...newMsgs,
        { role: 'assistant', content: "Couldn't process the follow-up. Please try again." },
      ];
      setMessages(withErr);
      if (userId) saveMessages(userId, activeSessionId, withErr);
    } finally {
      setFollowUpSubmitting(false);
    }
  };

  // ── Copy message ──────────────────────────────────────────────────────────
  const handleCopy = async (content: string, idx: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedIndex(idx);
      setTimeout(() => setCopiedIndex(prev => (prev === idx ? null : prev)), 2000);
    } catch {
      message.error('Failed to copy');
    }
  };

  const handleSaveMessageToLibrary = async (content: string, idx: number) => {
    if (!content.trim()) return;
    try {
      await client.createLibraryItem({
        source_type: 'CHAT',
        source_chat_id: activeSessionId || '',
        title: (sessions.find(s => s.id === activeSessionId)?.title || `Chat response ${idx + 1}`).slice(0, 160),
        summary: content.slice(0, 280),
        content_markdown: content,
        content_text: content,
        tags: ['chat'],
        metadata: { message_index: idx, session_id: activeSessionId },
      });
      message.success('Saved to Library');
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Unable to save to Library');
    }
  };

  // ── Keyboard send ─────────────────────────────────────────────────────────
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const railWidth = taskRailVisible ? 320 : 0;

  return (
    <div data-help="chat-workspace" style={{ display: 'flex', height: '100%', overflow: 'hidden', background: '#1a1a1a' }}>

      {/* ── Center: message stream + sender ──────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, position: 'relative' }}>

        {/* Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 16px',
            borderBottom: '1px solid #2a2a2a',
            background: '#1a1a1a',
            flexShrink: 0,
          }}
        >
          {/* KB selector — pill style */}
          {availableKbs.length > 0 && (
            <Select
              mode="multiple"
              size="small"
              placeholder={
                <span style={{ color: '#6b7280', fontSize: 12 }}>
                  <BookOutlined style={{ marginRight: 4 }} />Attach Knowledge Bases…
                </span>
              }
              value={chatKbIds}
              onChange={setChatKbIds}
              bordered={false}
              style={{ minWidth: 180, maxWidth: 400, fontSize: 12 }}
              options={availableKbs.map(kb => ({
                value: kb.id,
                label: `${kb.name}${kb.document_count != null ? ` (${kb.document_count})` : ''}`,
              }))}
              tagRender={({ label, value: v, closable, onClose }) => (
                <Tag
                  key={v}
                  closable={closable}
                  onClose={onClose}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    margin: '2px 2px',
                    padding: '2px 8px',
                    borderRadius: 12,
                    border: '1px solid #3f3f3f',
                    background: 'rgba(16,163,127,0.1)',
                    color: '#34d399',
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'default',
                  }}
                >
                  <BookOutlined style={{ fontSize: 10 }} />
                  {label}
                </Tag>
              )}
              maxTagCount={3}
              allowClear
              popupClassName="praxys-kb-select-popup"
            />
          )}
          {availableKbs.length === 0 && <span />}

          {/* Right actions */}
          <Space size={8}>
            {/* Task rail toggle */}
            {activeTask && (
              <Tooltip title={taskRailVisible ? 'Hide task panel' : 'Show task panel'}>
                <Button
                  type="text"
                  size="small"
                  icon={<RightOutlined rotate={taskRailVisible ? 0 : 180} />}
                  onClick={() => setTaskRailVisible(p => !p)}
                  style={{ color: '#8b949e' }}
                />
              </Tooltip>
            )}
            {/* Launch task */}
            <Button
              type="primary"
              size="small"
              icon={taskSubmitting ? <LoadingOutlined /> : <PlayCircleOutlined />}
              onClick={handleLaunchTask}
              disabled={taskSubmitting || chatSubmitting || !latestPrompt}
              style={{ fontSize: 12 }}
            >
              {taskSubmitting ? 'Launching…' : 'Launch Task'}
            </Button>
          </Space>
        </div>

        {/* Message stream */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px 0',
          }}
        >
          <div className="praxys-chat-column">
            {messages.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  paddingTop: 60,
                  color: '#6b7280',
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 16 }}>💬</div>
                <div style={{ fontSize: 16, color: '#c9d1d9', marginBottom: 8 }}>
                  Hi! I'm Praxys.
                </div>
                <div style={{ fontSize: 13 }}>
                  Ask me anything — or launch a task when you need automation.
                </div>
                {chatKbIds.length > 0 && (
                  <Tag color="green" style={{ marginTop: 12 }}>
                    📚 {chatKbIds.length} knowledge base{chatKbIds.length > 1 ? 's' : ''} active
                  </Tag>
                )}
              </div>
            )}

            {messages.map((msg, idx) => {
              const isUser = msg.role === 'user';
              const isTaskNotice =
                !isUser &&
                activeTask?.messageIndex === idx - 1 &&
                msg.content.startsWith('🚀');

              return (
                <div
                  key={idx}
                  ref={(node) => {
                    if (isUser) messageRefs.current[idx] = node;
                    else delete messageRefs.current[idx];
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: isUser ? 'row-reverse' : 'row',
                    marginBottom: 20,
                    gap: 10,
                    alignItems: 'flex-start',
                  }}
                >
                  {/* Avatar dot */}
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: isUser ? '#3b82f6' : '#10a37f',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {isUser ? (user?.name?.charAt(0).toUpperCase() ?? 'U') : 'P'}
                  </div>

                  {/* Bubble */}
                  <div style={{ width: isUser ? 'auto' : '100%', maxWidth: isUser ? '86%' : '100%', minWidth: 60 }}>
                    <div
                      style={{
                        background: isUser
                          ? '#2d5a99'
                          : isTaskNotice
                          ? '#1c2128'
                          : '#1e2736',
                        border: isUser
                          ? 'none'
                          : isTaskNotice
                          ? '1px solid #3b82f6'
                          : '1px solid #2d333b',
                        borderRadius: isUser ? '16px 16px 4px 16px' : '4px 16px 16px 16px',
                        padding: '10px 14px',
                        color: '#e6edf3',
                        fontSize: 14,
                        lineHeight: 1.65,
                      }}
                    >
                      {isUser ? (
                        <span style={{ whiteSpace: 'pre-wrap' }}>{msg.displayContent ?? msg.content}</span>
                      ) : (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            code: ({ className, children, ...props }) => {
                              const isBlock = className?.startsWith('language-');
                              return isBlock ? (
                                <pre style={{ background: '#0d1117', borderRadius: 6, padding: '10px 14px', overflowX: 'auto' }}>
                                  <code style={{ fontSize: 12, color: '#c9d1d9' }} {...props}>
                                    {children}
                                  </code>
                                </pre>
                              ) : (
                                <code style={{ background: '#161b22', padding: '1px 5px', borderRadius: 3, fontSize: 12 }} {...props}>
                                  {children}
                                </code>
                              );
                            },
                            a: ({ href, children, ...props }) => (
                              <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#58a6ff' }} {...props}>
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      )}
                    </div>

                    {/* Assistant message actions */}
                    {!isUser && (
                      <div
                        style={{
                          display: 'flex',
                          gap: 4,
                          marginTop: 6,
                          flexWrap: 'wrap',
                        }}
                      >
                        {/* Copy */}
                        <Tooltip title="Copy">
                          <Button
                            size="small"
                            icon={copiedIndex === idx ? <CheckOutlined /> : <CopyOutlined />}
                            onClick={() => handleCopy(msg.content, idx)}
                            style={getPillButtonStyle(copiedIndex === idx ? '#52c41a' : '#58a6ff')}
                          >
                            {copiedIndex === idx ? 'Copied' : 'Copy'}
                          </Button>
                        </Tooltip>

                        {/* Add to KB */}
                        <Tooltip title="Save to Knowledge Base">
                          <Button
                            size="small"
                            icon={<BookOutlined />}
                            onClick={() =>
                              setKbDraft({
                                open: true,
                                messageIndex: idx,
                                content: msg.content,
                              })
                            }
                            style={getPillButtonStyle('#58a6ff')}
                          >
                            Add to KB
                          </Button>
                        </Tooltip>

                        <Tooltip title="Save this response to Library">
                          <Button
                            size="small"
                            icon={<FileOutlined />}
                            onClick={() => handleSaveMessageToLibrary(msg.content, idx)}
                            style={getPillButtonStyle('#7c3aed')}
                          >
                            Save to Library
                          </Button>
                        </Tooltip>

                        {/* Create follow-up task */}
                        <Tooltip title="Launch a task based on this response">
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              setInput(`Follow-up on: ${msg.content.slice(0, 120)}`);
                              textareaRef.current?.focus();
                            }}
                            style={getPillButtonStyle('#10b981')}
                          >
                            Follow-up
                          </Button>
                        </Tooltip>

                        {/* Create Task from this message */}
                        <Tooltip title="Create an autonomous task from this response">
                          <Button
                            size="small"
                            icon={<ThunderboltOutlined />}
                            onClick={() => handleCreateTaskFromMessage(msg.content)}
                            style={getPillButtonStyle('#f59e0b')}
                          >
                            Create Task
                          </Button>
                        </Tooltip>

                        {/* Sources toggle (placeholder — real sources come from KB search) */}
                        {chatKbIds.length > 0 && (
                          <Tooltip title="View sources">
                            <Button
                              size="small"
                              icon={<LinkOutlined />}
                              onClick={() =>
                                setSources(prev =>
                                  prev.open && prev.messageIndex === idx
                                    ? { open: false, messageIndex: -1 }
                                    : { open: true, messageIndex: idx },
                                )
                              }
                              style={getPillButtonStyle('#9ca3af')}
                            >
                              Sources
                            </Button>
                          </Tooltip>
                        )}
                      </div>
                    )}

                    {/* Sources panel */}
                    {sources.open && sources.messageIndex === idx && (
                      <div
                        style={{
                          background: '#0d1117',
                          border: '1px solid #21262d',
                          borderRadius: 8,
                          padding: '10px 12px',
                          marginTop: 6,
                          fontSize: 11,
                          color: '#8b949e',
                        }}
                      >
                        <Text strong style={{ color: '#c9d1d9', fontSize: 11, display: 'block', marginBottom: 6 }}>
                          📚 Knowledge Base Sources
                        </Text>
                        <Text style={{ color: '#6b7280', fontSize: 11 }}>
                          Sources were retrieved from the selected knowledge bases for context.
                          Exact excerpts are included in the system prompt but not shown here.
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Typing indicator */}
            {chatSubmitting && (
              <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#10a37f',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  P
                </div>
                <div
                  style={{
                    background: '#1e2736',
                    border: '1px solid #2d333b',
                    borderRadius: '4px 16px 16px 16px',
                    padding: '10px 14px',
                  }}
                >
                  <LoadingOutlined style={{ color: '#3b82f6', fontSize: 18 }} />
                </div>
              </div>
            )}

            {followUpSubmitting && (
              <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#10a37f',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  P
                </div>
                <Skeleton active paragraph={{ rows: 2 }} style={{ maxWidth: 300 }} />
              </div>
            )}

            <div ref={bottomRef} />
          </div>
        </div>

        {questionMarkers.length > 0 && (
          <div className="praxys-chat-index-rail" aria-label="Question index">
            <div className="praxys-chat-index-line" />
            {questionMarkers.map(({ msg, index }, markerIndex) => (
              <Tooltip
                key={index}
                placement="left"
                title={(msg.displayContent || msg.content || `Question ${markerIndex + 1}`).slice(0, 120)}
              >
                <button
                  type="button"
                  className="praxys-chat-index-marker"
                  style={{
                    top: questionMarkers.length === 1
                      ? '50%'
                      : `${(markerIndex / (questionMarkers.length - 1)) * 100}%`,
                  }}
                  onClick={() => scrollToQuestion(index)}
                  aria-label={`Go to question ${markerIndex + 1}`}
                />
              </Tooltip>
            ))}
          </div>
        )}

        {/* ── Sender ─────────────────────────────────────────────────────── */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #2a2a2a',
            background: '#1a1a1a',
            flexShrink: 0,
          }}
        >
          <div
            className="praxys-chat-column"
            style={{
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: 12,
              overflow: 'hidden',
              transition: 'border-color 0.15s',
            }}
            onFocus={() => {}}
          >
            {attachedFile && (
              <div className="praxys-attached-file" style={{ margin: '8px 10px 0' }}>
                <PaperClipOutlined style={{ marginRight: 6 }} />
                <span className="praxys-attached-file-name">{attachedFile.name}</span>
                <button
                  className="praxys-attached-file-remove"
                  onClick={() => setAttachedFile(null)}
                  aria-label="Remove attachment"
                  type="button"
                >×</button>
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Praxys anything… (Shift+Enter for new line)"
              rows={2}
              disabled={chatSubmitting || followUpSubmitting}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                resize: 'none',
                color: '#ececec',
                fontSize: 14,
                padding: '12px 14px 8px',
                fontFamily: 'inherit',
                lineHeight: 1.5,
              }}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 10px',
                borderTop: '1px solid #333',
                gap: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {/* Attach file */}
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.txt,.csv,.json,.jsonl,.md,.xlsx,.sav,.pptx,.yaml,.yml,.log,.html,.xml,.js,.ts,.tsx,.jsx,.py,.css,.scss,.sql,.sh,.ini,.toml,.rtf,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tif,.tiff"
                  onChange={handleAttachFileChange}
                />
                <Tooltip title="Attach document or image">
                  <button
                    className="praxys-attach-btn"
                    onClick={handleAttachClick}
                    disabled={chatSubmitting || followUpSubmitting}
                    title="Attach document or image"
                    aria-label="Attach document or image"
                    type="button"
                  >
                    <PaperClipOutlined style={{ fontSize: 14 }} />
                  </button>
                </Tooltip>
                {/* Session file library */}
                <Tooltip title={`Session files (${sessionFiles.length})`}>
                  <button
                    className="praxys-attach-btn"
                    onClick={() => setLibraryDrawerOpen(true)}
                    aria-label="Session file library"
                    type="button"
                    style={sessionFiles.length > 0 ? { color: '#34d399' } : undefined}
                  >
                    <FolderOpenOutlined style={{ fontSize: 14 }} />
                  </button>
                </Tooltip>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Dictation mic */}
                <Tooltip title={dictationStatus === 'recording' ? 'Stop dictation' : 'Voice input'}>
                  <button
                    className={`praxys-dictation-btn${dictationStatus === 'recording' ? ' active' : ''}`}
                    onClick={handleDictationToggle}
                    disabled={chatSubmitting || followUpSubmitting || dictationStatus === 'loading'}
                    aria-label="Voice input"
                    type="button"
                  >
                    {dictationStatus === 'loading'
                      ? <LoadingOutlined style={{ fontSize: 14 }} />
                      : dictationStatus === 'recording'
                        ? <StopOutlined style={{ fontSize: 14 }} />
                        : <AudioOutlined style={{ fontSize: 14 }} />}
                  </button>
                </Tooltip>

                {/* Send */}
                <button
                  className={`praxys-send-btn${input.trim() || attachedFile ? ' active' : ''}`}
                  onClick={() => void handleSend()}
                  disabled={chatSubmitting || followUpSubmitting || (!input.trim() && !attachedFile)}
                  aria-label="Send"
                  type="button"
                >
                  {chatSubmitting ? <span className="praxys-spinner" /> : <SendOutlined style={{ fontSize: 14 }} />}
                </button>
              </div>
            </div>
          </div>
          <div className="praxys-chat-column" style={{ marginTop: 6, textAlign: 'center' }}>
            <Text style={{ color: '#555', fontSize: 11 }}>
              Praxys may make mistakes. For automation, use{' '}
              <span
                onClick={handleLaunchTask}
                style={{ color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Launch Task
              </span>.
            </Text>
          </div>
        </div>
      </div>

      {/* ── Right rail: task detail ────────────────────────────────────────── */}
      {taskRailVisible && activeTask && (
        <div
          style={{
            width: railWidth,
            flexShrink: 0,
            transition: 'width 0.2s ease',
            overflow: 'hidden',
          }}
        >
          <TaskDetailRail
            taskId={activeTask.taskId}
            onFollowUp={handleFollowUp}
            followUpSubmitting={followUpSubmitting}
          />
        </div>
      )}

      {/* ── KB Add Drawer ─────────────────────────────────────────────────── */}
      <KbAddDrawer
        open={kbDraft.open}
        onClose={() => setKbDraft(d => ({ ...d, open: false }))}
        userId={userId}
        draft={{
          suggestedTitle:
            [...messages]
              .slice(0, kbDraft.messageIndex)
              .reverse()
              .find(m => m.role === 'user')
              ?.content.slice(0, 120) ?? 'Chat Response',
          contentMarkdown: kbDraft.content,
          sourceModule: 'chat_workspace',
          sourceId: `${activeSessionId}-${kbDraft.messageIndex}`,
          tags: ['chat-result'],
        }}
        onSaved={() => {
          message.success('Saved to knowledge base');
          setKbDraft(d => ({ ...d, open: false }));
        }}
      />

      {/* Create Task from Message modal */}
      <Modal
        open={createTaskModal.open}
        title="Create Task from Response"
        onCancel={() => !createTaskModal.submitting && setCreateTaskModal(prev => ({ ...prev, open: false }))}
        onOk={handleSubmitTaskFromMessage}
        okText={createTaskModal.submitting ? 'Creating…' : 'Create Task'}
        okButtonProps={{ loading: createTaskModal.submitting, icon: <ThunderboltOutlined /> }}
        width={560}
        bodyStyle={{ background: '#1a1a1a', paddingTop: 8 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 4 }}>
          <div>
            <div style={{ color: '#8b949e', fontSize: 12, marginBottom: 4 }}>Task title</div>
            <Input
              value={createTaskModal.title}
              onChange={e => setCreateTaskModal(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter a task title…"
              style={{ background: '#0d1117', borderColor: '#30363d', color: '#e6edf3' }}
            />
          </div>
          <div>
            <div style={{ color: '#8b949e', fontSize: 12, marginBottom: 4 }}>Instructions (pre-filled from response)</div>
            <Input.TextArea
              value={createTaskModal.instructions}
              onChange={e => setCreateTaskModal(prev => ({ ...prev, instructions: e.target.value }))}
              rows={6}
              style={{ background: '#0d1117', borderColor: '#30363d', color: '#e6edf3', fontSize: 13 }}
            />
          </div>
          {chatKbIds.length > 0 && (
            <div style={{ color: '#8b949e', fontSize: 12 }}>
              📚 {chatKbIds.length} knowledge base{chatKbIds.length > 1 ? 's' : ''} will be linked to this task.
            </div>
          )}
        </div>
      </Modal>

      {/* Dictation overlay */}
      <DictationOverlay
        open={dictationStatus !== 'idle'}
        recording={dictationStatus === 'recording'}
        transcript={dictationTranscript}
        error={dictationError || null}
        permissionDenied={dictationStatus === 'permission-denied'}
        onStop={handleDictationToggle}
        onCancel={handleDictationToggle}
      />

      {/* Session file library drawer */}
      <Drawer
        title={
          <span style={{ color: '#e6edf3', fontSize: 14, fontWeight: 600 }}>
            <FolderOpenOutlined style={{ marginRight: 8, color: '#34d399' }} />
            Session Files ({sessionFiles.length})
          </span>
        }
        placement="right"
        width={360}
        open={libraryDrawerOpen}
        onClose={() => setLibraryDrawerOpen(false)}
        bodyStyle={{ background: '#1a1a1a', padding: '12px 16px' }}
        headerStyle={{ background: '#1a1a1a', borderBottom: '1px solid #2f2f2f' }}
        closeIcon={<CloseOutlined style={{ color: '#8b949e' }} />}
      >
        {sessionFiles.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#555', marginTop: 48, fontSize: 13 }}>
            No files attached yet in this session.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sessionFiles.map(sf => (
              <div
                key={sf.id}
                style={{
                  background: '#242424',
                  border: '1px solid #2f2f2f',
                  borderRadius: 8,
                  padding: '10px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileOutlined style={{ color: '#34d399', fontSize: 16, flexShrink: 0 }} />
                  <span style={{ color: '#e6edf3', fontSize: 13, fontWeight: 500, wordBreak: 'break-all' }}>
                    {sf.name}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#6b7280', paddingLeft: 24 }}>
                  <span>{sf.type || 'file'}</span>
                  <span>{(sf.size / 1024).toFixed(1)} KB</span>
                  <span>{new Date(sf.addedAt).toLocaleTimeString()}</span>
                </div>
                {sf.driveUrl && (
                  <a
                    href={sf.driveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#34d399', fontSize: 11, paddingLeft: 24, textDecoration: 'none' }}
                  >
                    📂 View in Google Drive
                  </a>
                )}
                <div style={{ paddingLeft: 24, fontSize: 11, color: '#555' }}>
                  {sf.contextTitle}
                </div>
              </div>
            ))}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default ChatWorkspace;
